import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  removeXdrSuppression,
  saveXdrPolicy,
  xdrPolicyQuery,
  xdrSuppressionsQuery,
  XDR_POLICY_FIELDS,
  XDR_POLICY_KEY,
  XDR_SUPPRESSIONS_KEY,
  type XdrAction,
  type XdrEngine,
  type XdrEngineStatus,
  type XdrPolicyField,
  type XdrPolicyPage,
  type XdrPolicySave,
  type XdrRule,
  type XdrSuppression,
} from '../../api/securiteeXdrPolicy'
import { flowAge } from '../../securitee/XdrFlowAlert'
import { Spinner } from '../../ui/Spinner'
import { XdrSection } from './XdrSection'
import './xdrEngine.css'

/**
 * Settings > Securitee > XDR - regulile XDR ale firmei, sub colectare: motorul de detectie,
 * incidente si tichete, raspuns, actiunea recomandata pe regula, investigatie, suprimari.
 * Fiecare sectiune salveaza tot randul firmei, cu partea ei schimbata.
 */

const SEVERITIES: Array<[number, string]> = [
  [0, 'Low'],
  [7, 'Medium'],
  [12, 'High'],
  [14, 'Critical'],
]

function severityOf(level: number): string {
  return level >= 14 ? 'Critical' : level >= 12 ? 'High' : level >= 7 ? 'Medium' : 'Low'
}

const FIELD_TEXT: Record<XdrPolicyField, { label: string; unit: string; info: string }> = {
  ticket_min_level: {
    label: 'Open a ticket automatically from',
    unit: '',
    info: 'Incidents at this severity or higher get an Easeedesk ticket the moment they are created. Lower ones stay in Securitee > Incidents only.',
  },
  group_window_hours: {
    label: 'Group detections into one incident for',
    unit: 'hours',
    info: 'Detections on the same device within this time join the open incident. While the incident ticket is open, new detections always join it.',
  },
  ticket_grace_minutes: {
    label: 'Show "Not opened" after',
    unit: 'minutes',
    info: 'An incident that should have a ticket and still has none after this time gets the red "Not opened" band in Incidents.',
  },
  no_answer_minutes: {
    label: 'Show "No answer" after',
    unit: 'minutes',
    info: 'An action the agent picked up but did not answer in this time is shown as "No answer" on the ticket.',
  },
  action_expiry_minutes: {
    label: 'Lock screen, Log off, Stop process expire after',
    unit: 'minutes',
    info: 'If the device does not pick the action up in this time (offline, asleep), it is dropped instead of running later on someone else.',
  },
  power_expiry_minutes: {
    label: 'Shut down, Restart expire after',
    unit: 'minutes',
    info: 'The same, for shut down and restart. Isolation never expires: a device isolated while offline is isolated when it comes back.',
  },
  timeline_before_minutes: {
    label: 'Timeline: minutes before the detection',
    unit: 'minutes',
    info: 'How much of what happened on the device before the detection the ticket timeline shows.',
  },
  timeline_after_minutes: {
    label: 'Timeline: minutes after the detection',
    unit: 'minutes',
    info: 'How much of what happened after the detection the ticket timeline shows.',
  },
  sweep_days: {
    label: '"Where else?" looks back',
    unit: 'days',
    info: 'How far back the search for the same file, IP address or path on the other devices of the company goes.',
  },
  false_positive_undo_minutes: {
    label: 'Undo a false positive for',
    unit: 'minutes',
    info: 'After "False positive" in Incidents, the incident can be reopened with Undo for this long. Then the decision stays.',
  },
  log_retention_months: {
    label: 'Keep the security log for',
    unit: 'months',
    info: 'The period this company promises to keep its security log (NIS2, CyFun DE.AE-03.1). OD itself keeps the EDR log 30 to 61 days; a longer target shows on the NIS2 card until an archive or your own SIEM keeps it.',
  },
  suppress_days: {
    label: '"Suppress similar" lasts by default',
    unit: 'days',
    info: 'The length offered when an analyst suppresses similar detections from a ticket. They can change it each time, up to 365 days.',
  },
}

const ACTION_LABELS: Record<XdrAction, string> = {
  isolate: 'Isolate (full)',
  isolate_internal: 'Isolate from LAN',
  isolate_external: 'Isolate from internet',
  lock: 'Lock screen',
  logoff: 'Log off',
  shutdown: 'Shut down',
  block_ip: 'Block the source IP',
  disable_account: 'Disable the account',
  none: 'Nothing on the device',
}

const ACTIONS = Object.keys(ACTION_LABELS) as XdrAction[]

const ENGINE_LABELS: Record<XdrEngine, string> = {
  native: 'OptimumDesk (built-in)',
  wazuh: 'Wazuh SIEM',
}

const ENGINES = Object.keys(ENGINE_LABELS) as XdrEngine[]

const ENGINE_TITLES: Record<XdrEngine, string> = {
  native: 'OptimumDesk',
  wazuh: 'Wazuh SIEM',
}

const ENGINE_TEXT: Record<XdrEngine, string> = {
  native: 'OptimumDesk evaluates the XDR rules itself. Detections, incidents and tickets are created here. Nothing is sent to Wazuh.',
  wazuh: 'Events are forwarded to your Wazuh. Detections appear only after Wazuh raises an alert.',
}

function engineOf(page: XdrPolicyPage): XdrEngine {
  return page.values.detection_engine ?? 'native'
}

interface FieldError {
  field: string
  message: string
}

function ownText(page: XdrPolicyPage, field: XdrPolicyField): string {
  return page.own[field] === null ? '' : String(page.own[field])
}

function changedRules(rules: XdrRule[], draft: Record<string, XdrAction> = {}): Record<string, XdrAction> {
  const out: Record<string, XdrAction> = {}
  for (const r of rules) {
    const action = draft[r.id] ?? r.action
    if (action !== r.default) out[r.id] = action
  }
  return out
}

/** Tot randul firmei: ce e in sectiune din formular, restul cum e salvat. Gol = implicit; ce nu e numar pleaca asa, ca serverul sa-l refuze. */
function bodyOf(page: XdrPolicyPage, draft: Partial<Record<XdrPolicyField, string>>, rules?: Record<string, XdrAction>): XdrPolicySave {
  const out = {} as XdrPolicySave
  for (const field of XDR_POLICY_FIELDS) {
    const text = (draft[field] ?? ownText(page, field)).trim()
    out[field] = text === '' ? null : /^\d+$/.test(text) ? Number(text) : text
  }
  out.recommended = changedRules(page.rules, rules)
  out.detection_engine = page.own.detection_engine ?? null
  return out
}

function errorOf(err: unknown): FieldError {
  const details = err instanceof ApiError ? err.details : undefined
  return { field: typeof details?.field === 'string' ? details.field : '', message: err instanceof Error ? err.message : 'Something went wrong.' }
}

function Info({ text }: { text: string }) {
  return <i className="fas fa-info-circle xdr-i" title={text} role="img" aria-label={text} style={{ marginLeft: 6, color: '#8a99a8' }}></i>
}

function Updated({ page }: { page: XdrPolicyPage }) {
  if (page.updated === null) return null
  return (
    <p className="text-muted xdr-updated" style={{ fontSize: 12, marginTop: 10 }}>
      Last changed {page.updated.at}
      {page.updated.by !== '' ? ` by ${page.updated.by}` : ''}.
    </p>
  )
}

function SaveRow({ title, canEdit, dirty, busy, saved, error }: { title: string; canEdit: boolean; dirty: boolean; busy: boolean; saved: boolean; error: FieldError | null }) {
  return (
    <>
      {error && <div className="alert alert-danger">{error.message}</div>}
      {saved && !error && <div className="alert alert-success xdr-saved">Saved.</div>}
      {canEdit && (
        <button type="submit" className="btn btn-primary" disabled={!dirty || busy} aria-label={`Save ${title}`}>
          Save
        </button>
      )}
    </>
  )
}

/** Partea comuna a unei sectiuni: salvarea, mesajul, eroarea. */
function useSave(onSaved: (next: XdrPolicyPage) => void) {
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<FieldError | null>(null)
  const run = async (body: XdrPolicySave) => {
    setBusy(true)
    setError(null)
    try {
      onSaved(await saveXdrPolicy(body))
      setSaved(true)
    } catch (err) {
      setError(errorOf(err))
    } finally {
      setBusy(false)
    }
  }
  const changed = () => {
    setSaved(false)
    setError(null)
  }
  return { busy, saved, error, run, changed }
}

function FieldsForm({ page, title, fields, onSaved }: { page: XdrPolicyPage; title: string; fields: XdrPolicyField[]; onSaved: (next: XdrPolicyPage) => void }) {
  const [draft, setDraft] = useState(() => Object.fromEntries(fields.map((f) => [f, ownText(page, f)])) as Record<XdrPolicyField, string>)
  const { busy, saved, error, run, changed } = useSave(onSaved)
  const canEdit = page.can_edit
  const dirty = fields.some((f) => draft[f].trim() !== ownText(page, f))

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (canEdit && dirty && !busy) void run(bodyOf(page, draft))
  }
  const set = (field: XdrPolicyField, value: string) => {
    setDraft({ ...draft, [field]: value })
    changed()
  }

  return (
    <form className="xdr-policy-form" noValidate onSubmit={submit}>
      <div className="row">
        {fields.map((field) => {
          const [def, min, max] = page.fields[field]
          const text = FIELD_TEXT[field]
          const id = `xdr-policy-${field}`
          const fieldError = error?.field === field ? error.message : null
          return (
            <div key={field} className={'col-sm-6 col-md-4 form-group' + (fieldError ? ' has-error' : '')}>
              <label htmlFor={id}>{text.label}</label>
              <Info text={text.info} />
              {field === 'ticket_min_level' ? (
                <select id={id} className="form-control" value={draft[field]} disabled={!canEdit || busy} onChange={(e) => set(field, e.target.value)}>
                  <option value="">Default ({severityOf(def)})</option>
                  {SEVERITIES.map(([level, name]) => (
                    <option key={level} value={String(level)}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={id}
                  type="number"
                  className="form-control"
                  min={min}
                  max={max}
                  step={1}
                  placeholder={String(def)}
                  value={draft[field]}
                  disabled={!canEdit || busy}
                  onChange={(e) => set(field, e.target.value)}
                />
              )}
              <span className="help-block">{fieldError ?? (field === 'ticket_min_level' ? `Default ${severityOf(def)}.` : `Default ${def} ${text.unit}. Between ${min} and ${max}.`)}</span>
            </div>
          )
        })}
      </div>
      <SaveRow title={title} canEdit={canEdit} dirty={dirty} busy={busy} saved={saved} error={error && fields.includes(error.field as XdrPolicyField) ? null : error} />
      <Updated page={page} />
    </form>
  )
}

/** Starea motorului sub carduri: text si cuvant de stare, nu numai culoare. */
function EngineStatusLine({ status }: { status?: XdrEngineStatus }) {
  if (!status) return null
  if (status.engine === 'wazuh') {
    return (
      <p className="xdr-engine-status is-idle" role="status">
        <i className="fas fa-share-square" aria-hidden="true"></i>
        <span>Events go to your Wazuh. Its health shows in the XDR flow banner on Incidents and Detections.</span>
      </p>
    )
  }
  if (status.stopped) {
    return (
      <p className="xdr-engine-status is-stopped" role="status">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
        <span>
          <strong>Stopped</strong> · new events have waited {flowAge(status.waiting_minutes ?? 0)} to be evaluated. New attacks will not raise incidents.
        </span>
      </p>
    )
  }
  const n = status.detections_24h
  return (
    <p className="xdr-engine-status is-ok" role="status">
      <i className="fas fa-check-circle" aria-hidden="true"></i>
      <span>
        <strong>Running</strong> · {n} {n === 1 ? 'detection' : 'detections'} in the last 24 h
      </span>
    </p>
  )
}

function EngineForm({ page, onSaved }: { page: XdrPolicyPage; onSaved: (next: XdrPolicyPage) => void }) {
  const [draft, setDraft] = useState<XdrEngine>(() => engineOf(page))
  const { busy, saved, error, run, changed } = useSave(onSaved)
  const canEdit = page.can_edit
  const dirty = draft !== engineOf(page)
  const fieldError = error?.field === 'detection_engine' ? error.message : null
  const status = page.engine_status
  // fara conexiune Wazuh, alegerea ar lasa firma fara nicio detectie; cine e deja pe Wazuh il pastreaza
  const wazuhBlocked = status !== undefined && !status.wazuh_ready && engineOf(page) !== 'wazuh'

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (canEdit && dirty && !busy) void run({ ...bodyOf(page, {}), detection_engine: draft })
  }

  return (
    <form className="xdr-engine-form" noValidate onSubmit={submit}>
      <fieldset className="xdr-engine-choice" disabled={!canEdit || busy}>
        <legend className="sr-only">Detection engine</legend>
        <div className="xdr-engine-options">
          {ENGINES.map((engine) => {
            const off = engine === 'wazuh' && wazuhBlocked
            const checked = draft === engine
            const id = `xdr-engine-${engine}`
            return (
              <label key={engine} className={'xdr-engine-card' + (checked ? ' is-checked' : '') + (off ? ' is-disabled' : '')}>
                <input
                  type="radio"
                  name="detection_engine"
                  value={engine}
                  checked={checked}
                  disabled={off}
                  aria-labelledby={`${id}-title`}
                  aria-describedby={off ? `${id}-text ${id}-why` : `${id}-text`}
                  onChange={() => {
                    setDraft(engine)
                    changed()
                  }}
                />
                <span>
                  <span id={`${id}-title`} className="xdr-engine-title">
                    {ENGINE_TITLES[engine]}
                    {engine === 'native' && <span className="xdr-engine-badge">Recommended</span>}
                  </span>
                  <span id={`${id}-text`} className="xdr-engine-text">
                    {ENGINE_TEXT[engine]}
                  </span>
                  {off && (
                    <span id={`${id}-why`} className="xdr-engine-why">
                      <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
                      <span>No Wazuh connection for this company. Set it up in SIEM groups first.</span>
                    </span>
                  )}
                </span>
              </label>
            )
          })}
        </div>
        {fieldError && (
          <span className="help-block text-danger" role="alert">
            {fieldError}
          </span>
        )}
      </fieldset>
      <EngineStatusLine status={status} />
      <SaveRow title="Detection engine" canEdit={canEdit} dirty={dirty} busy={busy} saved={saved} error={fieldError ? null : error} />
      <Updated page={page} />
    </form>
  )
}

function EdrBlindForm({ page, onSaved }: { page: XdrPolicyPage; onSaved: (next: XdrPolicyPage) => void }) {
  const [draft, setDraft] = useState(() => page.values.edr_blind)
  const { busy, saved, error, run, changed } = useSave(onSaved)
  const canEdit = page.can_edit
  const dirty = draft !== page.values.edr_blind

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (canEdit && dirty && !busy) void run({ ...bodyOf(page, {}), edr_blind: draft })
  }

  return (
    <form className="xdr-edr-blind-form" noValidate onSubmit={submit}>
      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={draft}
            disabled={!canEdit || busy}
            aria-describedby="xdr-edr-blind-help"
            onChange={(e) => {
              setDraft(e.target.checked)
              changed()
            }}
          />{' '}
          Raise an incident when a device stops sending events
        </label>
      </div>
      <p id="xdr-edr-blind-help" className="text-muted" style={{ fontSize: 12 }}>
        A device whose agent still checks in and reports processes, but sends no Windows or Linux events for longer than its usual pause (learned over
        the last 7 days, at least 1 hour), gets a Medium detection &quot;EDR events stopped&quot;, an incident and a ticket. Usually the Event Log or
        auditing was stopped. Nothing is done on the device automatically.
      </p>
      <SaveRow title="Devices that go quiet" canEdit={canEdit} dirty={dirty} busy={busy} saved={saved} error={error} />
    </form>
  )
}

function RulesForm({ page, title, onSaved }: { page: XdrPolicyPage; title: string; onSaved: (next: XdrPolicyPage) => void }) {
  const [draft, setDraft] = useState<Record<string, XdrAction>>(() => Object.fromEntries(page.rules.map((r) => [r.id, r.action])))
  const { busy, saved, error, run, changed } = useSave(onSaved)
  const canEdit = page.can_edit
  const dirty = page.rules.some((r) => draft[r.id] !== r.action)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (canEdit && dirty && !busy) void run(bodyOf(page, {}, draft))
  }

  return (
    <form className="xdr-rules-form" noValidate onSubmit={submit}>
      <p className="text-muted" style={{ fontSize: 12 }}>
        The suggested response on the ticket&apos;s XDR tab, for each detection rule. The analyst still confirms it: nothing runs on its own.
      </p>
      <div className="table-responsive">
        <table className="table table-striped xdr-rules">
          <thead>
            <tr>
              <th>Rule</th>
              <th style={{ width: 90 }}>Severity</th>
              <th style={{ width: 230 }}>Recommended action</th>
            </tr>
          </thead>
          <tbody>
            {page.rules.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'normal' }}>
                  <span className="text-muted">{r.id}</span> {r.name}
                </td>
                <td>{severityOf(r.level)}</td>
                <td>
                  <select
                    className="form-control input-sm"
                    value={draft[r.id]}
                    disabled={!canEdit || busy}
                    aria-label={`Recommended action for ${r.id} ${r.name}`}
                    onChange={(e) => {
                      setDraft({ ...draft, [r.id]: e.target.value as XdrAction })
                      changed()
                    }}
                  >
                    {ACTIONS.map((a) => (
                      <option key={a} value={a}>
                        {ACTION_LABELS[a]}
                        {a === r.default ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SaveRow title={title} canEdit={canEdit} dirty={dirty} busy={busy} saved={saved} error={error} />
      <Updated page={page} />
    </form>
  )
}

function SuppressionsList({ list, canEdit }: { list: XdrSuppression[]; canEdit: boolean }) {
  const client = useQueryClient()
  const [confirming, setConfirming] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const remove = async (id: number) => {
    setBusy(true)
    setError('')
    try {
      await removeXdrSuppression(id)
      setConfirming(null)
      await client.invalidateQueries({ queryKey: XDR_SUPPRESSIONS_KEY })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  if (list.length === 0) {
    return <p className="text-muted">No active suppressions. Analysts add them from a ticket, with Suppress similar.</p>
  }
  return (
    <>
      <p className="text-muted" style={{ fontSize: 12 }}>
        Detections that match a suppression do not create incidents or tickets until it expires. Remove one to get those alerts again.
      </p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="table-responsive">
        <table className="table table-striped xdr-suppressions">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Hides</th>
              <th>Reason</th>
              <th>Added</th>
              <th>Expires</th>
              <th>Matched</th>
              {canEdit && <th style={{ width: 170 }}></th>}
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.ID}>
                <td style={{ whiteSpace: 'normal' }}>{s.rule}</td>
                <td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  <span>
                    {s.field} = {s.value}
                  </span>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {s.channel}, event {s.event_id}
                  </div>
                </td>
                <td style={{ whiteSpace: 'normal' }}>{s.reason}</td>
                <td>
                  {s.created_at}
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {s.created_by}
                    {s.ticket_id !== null && (
                      <>
                        {s.created_by !== '' ? ', ' : ''}
                        <a href={`/app/easeedesk/ticket/${s.ticket_id}`}>Ticket #{s.ticket_id}</a>
                      </>
                    )}
                  </div>
                </td>
                <td>{s.expires_at}</td>
                <td>
                  <span>{s.hits}</span>
                  {s.last_hit_at !== null && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      last {s.last_hit_at}
                    </div>
                  )}
                </td>
                {canEdit && (
                  <td>
                    {confirming === s.ID ? (
                      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => void remove(s.ID)}>
                          Yes, remove
                        </button>
                        <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setConfirming(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button type="button" className="btn btn-default btn-xs" aria-label={`Remove suppression ${s.ID}`} onClick={() => setConfirming(s.ID)}>
                        Remove
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

const SECTIONS: Array<{ id: string; title: string; fields: XdrPolicyField[]; summary: (v: XdrPolicyPage['values']) => ReactNode }> = [
  {
    id: 'incidents',
    title: 'Incidents and tickets',
    fields: ['ticket_min_level', 'group_window_hours', 'ticket_grace_minutes', 'false_positive_undo_minutes'],
    summary: (v) =>
      `Ticket from ${severityOf(v.ticket_min_level)}, one incident per device for ${v.group_window_hours} h, "Not opened" after ${v.ticket_grace_minutes} min, Undo false positive ${v.false_positive_undo_minutes} min`,
  },
  {
    id: 'response',
    title: 'Response actions',
    fields: ['no_answer_minutes', 'action_expiry_minutes', 'power_expiry_minutes'],
    summary: (v) => `"No answer" after ${v.no_answer_minutes} min, Lock / Log off expire in ${v.action_expiry_minutes} min, Shut down / Restart in ${v.power_expiry_minutes} min`,
  },
  {
    id: 'retention',
    title: 'Security log retention',
    fields: ['log_retention_months'],
    summary: (v) => `Target ${v.log_retention_months} month${v.log_retention_months === 1 ? '' : 's'} - OD keeps 30 to 61 days`,
  },
  {
    id: 'investigation',
    title: 'Investigation',
    fields: ['timeline_before_minutes', 'timeline_after_minutes', 'sweep_days', 'suppress_days'],
    summary: (v) => `Timeline ${v.timeline_before_minutes} min before / ${v.timeline_after_minutes} after, "Where else?" ${v.sweep_days} days, Suppress ${v.suppress_days} days`,
  },
]

export function XdrPolicySections() {
  const client = useQueryClient()
  const policy = useQuery(xdrPolicyQuery())
  const suppressions = useQuery({ ...xdrSuppressionsQuery(), enabled: policy.isSuccess })

  // fara drept / fara firma: ecranul de sus a spus deja
  if (policy.error instanceof ApiError && (policy.error.status === 403 || policy.error.status === 409)) return null
  if (policy.error) return <div className="alert alert-danger">Something went wrong: {(policy.error as Error).message}</div>
  if (!policy.data) return <Spinner />

  const page = policy.data
  const onSaved = (next: XdrPolicyPage) => client.setQueryData(XDR_POLICY_KEY, next)
  const [incidents, response, retention, investigation] = SECTIONS
  const changed = Object.keys(changedRules(page.rules)).length
  const list = suppressions.data?.suppressions
  const section = (s: (typeof SECTIONS)[number]) => (
    <XdrSection key={s.id} id={s.id} title={s.title} summary={s.summary(page.values)}>
      <FieldsForm page={page} title={s.title} fields={s.fields} onSaved={onSaved} />
    </XdrSection>
  )

  return (
    <div className="xdr-policy">
      <XdrSection id="engine" title="Detection engine" summary={ENGINE_LABELS[engineOf(page)]}>
        <EngineForm page={page} onSaved={onSaved} />
      </XdrSection>
      <XdrSection id="edr-blind" title="Devices that go quiet" summary={page.values.edr_blind ? 'On' : 'Off'}>
        <EdrBlindForm page={page} onSaved={onSaved} />
      </XdrSection>
      {section(retention)}
      {section(incidents)}
      {section(response)}
      <XdrSection id="rules" title="Recommended action per rule" summary={`${page.rules.length} rules, ${changed === 0 ? 'all default' : `${changed} changed`}`}>
        <RulesForm page={page} title="Recommended action per rule" onSaved={onSaved} />
      </XdrSection>
      {section(investigation)}
      <XdrSection id="suppressions" title="Suppressions" summary={list === undefined ? '' : list.length === 0 ? 'None active' : `${list.length} active`}>
        {suppressions.error ? (
          <div className="alert alert-danger">Something went wrong: {(suppressions.error as Error).message}</div>
        ) : list === undefined ? (
          <Spinner />
        ) : (
          <SuppressionsList list={list} canEdit={page.can_edit} />
        )}
      </XdrSection>
    </div>
  )
}
